/**
 * Deduction panel — analytical reasoning chain.
 *
 * For the currently selected signal/risk item, shows the reasoning chain:
 * raw inputs (contributing events), module outputs (which analysis modules
 * flagged it), confidence factors, and final assessment.
 * Formatted as an indented chain: Input -> Module -> Factor -> Conclusion.
 */

import { eventBus } from '@/core/events';
import { getAnalysisEngine } from '@/intelligence/analysis/engine';
import type {
  AnalysisResult,
  FocalPoint,
  CompositeRisk,
  AggregatedSignal,
} from '@/intelligence/analysis/types';
import { relativeTime } from '@/utils/time';

type DeductionTarget =
  | { kind: 'focal-point'; data: FocalPoint }
  | { kind: 'risk'; data: CompositeRisk }
  | { kind: 'signal'; data: AggregatedSignal };

export function createDeductionPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'deduction-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let currentTarget: DeductionTarget | null = null;
  let renderPending = false;

  function scheduleRender(): void {
    if (!currentTarget || renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      if (currentTarget) render(currentTarget);
    });
  }

  function renderEmpty(): void {
    contentArea.textContent = '';
    const msg = document.createElement('div');
    msg.style.cssText = 'padding: 32px 16px; text-align: center; color: var(--text-muted); font-size: 12px;';
    msg.textContent = 'Select a signal or focal point to view reasoning chain';
    contentArea.appendChild(msg);
  }

  function renderSectionHeader(text: string, indent: number = 0): HTMLElement {
    const header = document.createElement('div');
    header.style.cssText = `
      font-size: 11px; font-weight: 600; color: var(--text-secondary);
      margin-bottom: 4px; margin-top: 8px; text-transform: uppercase;
      letter-spacing: 0.05em; margin-left: ${indent}px;
    `;
    header.textContent = text;
    return header;
  }

  function renderChainStep(text: string, depth: number, color?: string): HTMLElement {
    const row = document.createElement('div');
    row.style.cssText = `
      display: flex; align-items: flex-start; gap: 6px; padding: 2px 8px;
      margin-bottom: 2px; margin-left: ${depth * 16}px;
    `;

    const connector = document.createElement('span');
    connector.style.cssText = `font-size: 10px; color: var(--text-muted); flex-shrink: 0; margin-top: 1px;`;
    connector.textContent = depth === 0 ? '\u25CF' : '\u251C\u2500';
    row.appendChild(connector);

    const label = document.createElement('span');
    label.style.cssText = `font-size: 11px; color: ${color ?? 'var(--text-primary)'};`;
    label.textContent = text;
    row.appendChild(label);

    return row;
  }

  function render(target: DeductionTarget): void {
    const engine = getAnalysisEngine();
    const result = engine?.getLastResult() ?? null;

    contentArea.textContent = '';

    switch (target.kind) {
      case 'focal-point':
        renderFocalPointChain(target.data, result);
        break;
      case 'risk':
        renderRiskChain(target.data, result);
        break;
      case 'signal':
        renderSignalChain(target.data, result);
        break;
    }
  }

  function renderFocalPointChain(fp: FocalPoint, result: AnalysisResult | null): void {
    // Title
    const title = document.createElement('h3');
    title.style.cssText = 'margin: 0 0 12px 0; font-size: 14px; font-weight: 600; color: var(--text-primary);';
    title.textContent = `Focal Point: ${fp.entity}`;
    contentArea.appendChild(title);

    // 1. Raw inputs
    contentArea.appendChild(renderSectionHeader('\u2460 Raw Inputs'));
    for (const sig of fp.signals) {
      contentArea.appendChild(
        renderChainStep(`[${sig.type}] ${sig.description} (${sig.severity})`, 1),
      );
      contentArea.appendChild(
        renderChainStep(`at ${relativeTime(sig.timestamp)}`, 2, 'var(--text-muted)'),
      );
    }

    // 2. Module outputs
    contentArea.appendChild(renderSectionHeader('\u2461 Analysis Modules'));
    contentArea.appendChild(
      renderChainStep(`Focal Point Detection: ${fp.mentionCount} mentions`, 1),
    );
    contentArea.appendChild(
      renderChainStep(`News score: ${fp.newsScore}`, 2, 'var(--text-muted)'),
    );
    contentArea.appendChild(
      renderChainStep(`Signal score: ${fp.signalScore}`, 2, 'var(--text-muted)'),
    );
    contentArea.appendChild(
      renderChainStep(`Correlation bonus: +${fp.correlationBonus}`, 2, 'var(--text-muted)'),
    );

    // Check if any surge is related
    if (result) {
      const relatedSurges = result.surges.filter(s =>
        s.region.toLowerCase().includes(fp.entity.toLowerCase()),
      );
      if (relatedSurges.length > 0) {
        contentArea.appendChild(
          renderChainStep(`Surge Detection: ${relatedSurges.length} related surge(s)`, 1),
        );
      }
    }

    // 3. Confidence factors
    contentArea.appendChild(renderSectionHeader('\u2462 Confidence Factors'));
    contentArea.appendChild(
      renderChainStep(`${fp.signals.length} contributing signals`, 1),
    );
    contentArea.appendChild(
      renderChainStep(`${fp.mentionCount} entity mentions`, 1),
    );
    contentArea.appendChild(
      renderChainStep(`Correlation bonus: ${fp.correlationBonus > 0 ? 'present (boosted)' : 'none'}`, 1, fp.correlationBonus > 0 ? 'var(--severity-high)' : 'var(--text-muted)'),
    );

    // 4. Conclusion
    contentArea.appendChild(renderSectionHeader('\u2463 Final Assessment'));
    const urgencyColor = fp.urgencyLevel === 'critical'
      ? 'var(--severity-critical)'
      : fp.urgencyLevel === 'elevated'
        ? 'var(--severity-high)'
        : 'var(--severity-medium)';
    contentArea.appendChild(
      renderChainStep(`Composite score: ${fp.score}`, 1),
    );
    contentArea.appendChild(
      renderChainStep(`Urgency: ${fp.urgencyLevel.toUpperCase()}`, 1, urgencyColor),
    );
    if (fp.narrative) {
      contentArea.appendChild(
        renderChainStep(`Narrative: ${fp.narrative}`, 1, 'var(--text-secondary)'),
      );
    }
  }

  function renderRiskChain(risk: CompositeRisk, _result: AnalysisResult | null): void {
    const title = document.createElement('h3');
    title.style.cssText = 'margin: 0 0 12px 0; font-size: 14px; font-weight: 600; color: var(--text-primary);';
    title.textContent = `Composite Risk: ${risk.regionName}`;
    contentArea.appendChild(title);

    // 1. Component inputs
    contentArea.appendChild(renderSectionHeader('\u2460 Component Scores'));
    contentArea.appendChild(
      renderChainStep(`CII: ${Math.round(risk.components.cii)}/100 (weight 50%)`, 1),
    );
    contentArea.appendChild(
      renderChainStep(`Convergence: ${Math.round(risk.components.convergence)}/100 (weight 30%)`, 1),
    );
    contentArea.appendChild(
      renderChainStep(`Infrastructure: ${Math.round(risk.components.infrastructure)}/100 (weight 20%)`, 1),
    );

    // 2. Boosts
    contentArea.appendChild(renderSectionHeader('\u2461 Boost Factors'));
    if (risk.boosts.theaterPosture > 0) {
      contentArea.appendChild(
        renderChainStep(`Theater posture: +${risk.boosts.theaterPosture}`, 1, 'var(--severity-high)'),
      );
    } else {
      contentArea.appendChild(
        renderChainStep('Theater posture: none', 1, 'var(--text-muted)'),
      );
    }
    if (risk.boosts.breakingNews > 0) {
      contentArea.appendChild(
        renderChainStep(`Breaking news: +${risk.boosts.breakingNews}`, 1, 'var(--severity-high)'),
      );
    } else {
      contentArea.appendChild(
        renderChainStep('Breaking news: none', 1, 'var(--text-muted)'),
      );
    }

    // 3. Conclusion
    contentArea.appendChild(renderSectionHeader('\u2462 Final Assessment'));
    const scoreColor = risk.score >= 70 ? 'var(--severity-critical)'
      : risk.score >= 50 ? 'var(--severity-high)'
        : risk.score >= 30 ? 'var(--severity-medium)'
          : 'var(--severity-low)';
    contentArea.appendChild(
      renderChainStep(`Composite score: ${Math.round(risk.score)}/100`, 1, scoreColor),
    );
    contentArea.appendChild(
      renderChainStep(`Detected: ${relativeTime(risk.detectedAt)}`, 1, 'var(--text-muted)'),
    );
  }

  function renderSignalChain(signal: AggregatedSignal, result: AnalysisResult | null): void {
    const title = document.createElement('h3');
    title.style.cssText = 'margin: 0 0 12px 0; font-size: 14px; font-weight: 600; color: var(--text-primary);';
    title.textContent = `Signal: ${signal.country} (${signal.countryCode})`;
    contentArea.appendChild(title);

    // 1. Signal types
    contentArea.appendChild(renderSectionHeader('\u2460 Signal Composition'));
    for (const sig of signal.signals) {
      contentArea.appendChild(
        renderChainStep(`${sig.type}: ${sig.count} events (${sig.severity})`, 1),
      );
    }

    // 2. Module flags
    contentArea.appendChild(renderSectionHeader('\u2461 Analysis Module Flags'));

    if (result) {
      const hasFP = result.focalPoints.some(fp =>
        fp.entity.toLowerCase().includes(signal.country.toLowerCase()),
      );
      contentArea.appendChild(
        renderChainStep(`Focal Point: ${hasFP ? 'FLAGGED' : 'not flagged'}`, 1, hasFP ? 'var(--severity-high)' : 'var(--text-muted)'),
      );

      const hasConvergence = result.convergences.some(c =>
        c.label.toLowerCase().includes(signal.country.toLowerCase()),
      );
      contentArea.appendChild(
        renderChainStep(`Convergence: ${hasConvergence ? 'ACTIVE' : 'none'}`, 1, hasConvergence ? 'var(--severity-high)' : 'var(--text-muted)'),
      );

      const cii = result.ciiScores.get(signal.countryCode.toUpperCase());
      if (cii) {
        contentArea.appendChild(
          renderChainStep(`CII: ${cii.overall}/100 (${cii.trend})`, 1),
        );
      }
    }

    // 3. Convergence zone
    contentArea.appendChild(renderSectionHeader('\u2462 Convergence Assessment'));
    contentArea.appendChild(
      renderChainStep(`Score: ${signal.convergenceScore}`, 1),
    );
    contentArea.appendChild(
      renderChainStep(`Zone: ${signal.zone ?? 'none detected'}`, 1, signal.zone ? 'var(--severity-medium)' : 'var(--text-muted)'),
    );
  }

  // Initial empty state
  renderEmpty();

  // Listen for analysis results (to update currently shown chain)
  cleanupFns.push(
    eventBus.on('analysis:complete', () => scheduleRender()),
  );

  // Listen for focal points
  cleanupFns.push(
    eventBus.on('analysis:focal-point', (fp) => {
      currentTarget = { kind: 'focal-point', data: fp };
      render(currentTarget);
    }),
  );

  // Listen for risk
  cleanupFns.push(
    eventBus.on('analysis:risk', (risk) => {
      currentTarget = { kind: 'risk', data: risk };
      render(currentTarget);
    }),
  );

  // Listen for signals
  cleanupFns.push(
    eventBus.on('signal:inspect', (signal) => {
      currentTarget = { kind: 'signal', data: signal };
      render(currentTarget);
    }),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
